import { useMutation } from '@apollo/client';
import { Button, Flex, Modal, Paper, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { allQuestionsDeletedEvent } from '../../../../events';
import { SettingsClearQuestions } from './SettingsClearQuestionsMutation';

interface SettingsClearQuestionsModalProps {
	opened: boolean;
	onRequestClose: () => void;
}

export default function SettingsClearQuestionsModal({
	opened,
	onRequestClose,
}: SettingsClearQuestionsModalProps) {
	const { t } = useTranslation();

	const [clearQuestions, { loading }] = useMutation(SettingsClearQuestions);

	async function handleClearQuestions() {
		try {
			const { data } = await clearQuestions();
			const { success, errors } = data?.clearQuestions ?? {};

			if (!success || errors?.length) {
				notifications.show({
					message: t('common.error.unknown'),
					color: 'red',
				});
				return;
			}

			onRequestClose();
			allQuestionsDeletedEvent.publish({});
			notifications.show({
				message: t('settings.accountSettings.clearQuestions.modal.success'),
				color: 'teal',
			});
		}
		catch (e) {
			notifications.show({
				message: t('common.error.unknown'),
				color: 'red',
			});
		}
	}

	return (
		<Modal
			centered
			size="sm"
			opened={opened}
			onClose={onRequestClose}
			title={<Text fz="sm">{t('settings.accountSettings.clearQuestions.modal.title')}</Text>}
		>
			<Stack mx="sm" mb="md" gap="sm">
				<Paper bg="dark.9" px="sm" py="md">
					<Stack>
						<Text fz="sm">
							{t('settings.accountSettings.clearQuestions.modal.description')}
						</Text>
					</Stack>
				</Paper>

				<Flex justify="flex-end" gap="xs">
					<Button
						size="xs"
						color="gray"
						variant="subtle"
						disabled={loading}
						onClick={onRequestClose}
					>
						{t('settings.accountSettings.clearQuestions.modal.cancel')}
					</Button>

					<Button
						size="xs"
						color="red.8"
						loading={loading}
						onClick={handleClearQuestions}
					>
						{t('settings.accountSettings.clearQuestions.modal.delete')}
					</Button>
				</Flex>
			</Stack>
		</Modal>
	);
}
